import { Download, EllipsisVertical, ExternalLink, Printer } from 'lucide-react';

import { Button } from '@/components/atoms/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';

/**
 * The full-width letterhead bar atop the single-scope estimate: tenant brand on
 * the left, the estimate number and document actions (print / download / portal)
 * on the right — mirroring the artifact's app-bar. Logo is data-gated; the mark
 * falls back to the company initial.
 */
export function SingleScopeLetterhead({
  companyName,
  logoUrl,
  estimateNumber,
  onPrint,
  onDownload,
  portalHref,
}: {
  companyName: string;
  logoUrl?: string | null;
  estimateNumber?: string | null;
  onPrint?: () => void;
  onDownload?: () => void;
  portalHref?: string | null;
}) {
  return (
    // The artifact's .pubtop: a sticky, translucent bone bar that blurs the
    // document scrolling beneath it. The left brand shows only below lg — on
    // large screens the persistent rail owns the tenant's identity.
    <div className="sticky top-0 z-30 border-b border-[#14141A]/[0.08] bg-[#F6F6F4]/[0.92] backdrop-blur supports-[backdrop-filter]:bg-[#F6F6F4]/[0.82]">
      <div className="flex min-h-[60px] items-center gap-3 px-4 py-2.5 sm:px-6 lg:px-[30px]">
        {/* Left slot grows to push the actions right (the artifact's
            `.pubtop__t{flex:1}`): the brand fills it below lg, the left-aligned
            "Estimate NNN" label fills it at lg and up. */}
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="flex min-w-0 items-center gap-3 lg:hidden">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={companyName}
                className="size-9 shrink-0 rounded-lg object-contain"
              />
            ) : (
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[#17171A] text-sm font-bold text-white">
                {companyName.charAt(0).toUpperCase()}
              </span>
            )}
            <span className="truncate font-semibold text-[#17171A]">{companyName}</span>
          </div>
          {estimateNumber ? (
            <span className="hidden truncate text-[10.5px] font-semibold uppercase tracking-[0.07em] text-[#95969D] lg:inline">
              Estimate {estimateNumber}
            </span>
          ) : null}
        </div>

        {onPrint || onDownload || portalHref ? (
          <div className="shrink-0 sm:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="icon" aria-label="Document options">
                  <EllipsisVertical className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onPrint ? (
                  <DropdownMenuItem onSelect={onPrint}>
                    <Printer /> Print estimate
                  </DropdownMenuItem>
                ) : null}
                {onDownload ? (
                  <DropdownMenuItem onSelect={onDownload}>
                    <Download /> Download estimate
                  </DropdownMenuItem>
                ) : null}
                {portalHref ? (
                  <DropdownMenuItem asChild>
                    <a href={portalHref}>
                      <ExternalLink /> Access your portal
                    </a>
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : null}
        <div className="hidden shrink-0 items-center gap-1.5 sm:flex">
          {onPrint ? (
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Print estimate"
              onClick={onPrint}
            >
              <Printer className="size-4" />
            </Button>
          ) : null}
          {onDownload ? (
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Download estimate"
              onClick={onDownload}
            >
              <Download className="size-4" />
            </Button>
          ) : null}
          {portalHref ? (
            <Button asChild variant="outline" size="sm">
              <a href={portalHref}>Access your portal</a>
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
